import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/adminlogin';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password, captchaToken) => api.post('/auth/login', { email, password, captchaToken }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Wraps a `?days=N` route so callers can optionally pass an additional
// params object (release_op / release_value / layer) from the
// ReleaseFilterBar. Backwards compatible — `getX(7)` still works.
function withReleaseParams(path, days, extra) {
  const params = { ...(extra || {}) };
  if (days != null) params.days = days;
  return api.get(path, { params });
}

export const analyticsAPI = {
  getSummary: (timeframe = '7d', extra) =>
    api.get('/analytics/summary', { params: { timeframe, ...(extra || {}) } }),
  getSessions: (limit = 20, offset = 0) =>
    api.get(`/analytics/sessions?limit=${limit}&offset=${offset}`),
  getSessionDetails: (sessionId) => api.get(`/analytics/sessions/${sessionId}`),
  getSession: (sessionId) => api.get(`/analytics/session/${sessionId}`),
  getTimeSeries: (days = 7, extra) => withReleaseParams('/analytics/timeseries', days, extra),
  exportCSV: () => api.get('/analytics/export', { responseType: 'blob' }),
  getStorageErrors: (days = 30, extra) => withReleaseParams('/analytics/storage-errors', days, extra),
  getDiscoveryCensus: (days = 7, extra) => withReleaseParams('/analytics/discovery-census', days, extra),
  getTranscodeEvents: (days = 30, extra) => withReleaseParams('/analytics/transcode-events', days, extra),
  getRemoteSourceEvents: (days = 30, extra) => withReleaseParams('/analytics/remote-source-events', days, extra),
  getCcDisconnectReasons: (days = 30, extra) => withReleaseParams('/analytics/cc-disconnect-reasons', days, extra),
  getPipelineCcCompat: (days = 30, extra) => withReleaseParams('/analytics/pipeline/cc-compat', days, extra),
  getPipelineConnectionDialogs: (days = 30, extra) => withReleaseParams('/analytics/pipeline/connection-dialogs', days, extra),
  getPipelineDmsTranscode: (days = 30, extra) => withReleaseParams('/analytics/pipeline/dms-transcode', days, extra),
  getPipelineDlnaTranscode: (days = 30, extra) => withReleaseParams('/analytics/pipeline/dlna-transcode', days, extra),
  getPipelineTranscodeOffer: (days = 30, extra) => withReleaseParams('/analytics/pipeline/transcode-offer', days, extra),
  // v1.12.0.18 — browser-cast pipeline (renderer-gone, cursor-anchor,
  // disconnect reason_code, relay state changes, foreground-service
  // state, receiver-side errors). See backend/routes/analytics_pipeline.js
  // and schema/010_browser_cast_views.sql.
  getPipelineBrowserCast: (days = 30, extra) => withReleaseParams('/analytics/pipeline/browser-cast', days, extra),
};

export const visitorsAPI = {
  getSummary: (timeframe = '7d') => api.get(`/analytics/visitors/summary?timeframe=${timeframe}`),
  getTimeSeries: (days = 7) => api.get(`/analytics/visitors/timeseries?days=${days}`),
};

export const cloudAPI = {
    getOverview: () => api.get('/admin/api/cloud/overview'),
    getGrowth: (range = '30d') => api.get(`/admin/api/cloud/growth?range=${range}`),
    getRetention: () => api.get('/admin/api/cloud/retention'),
    getRevenue: (range = '6m') => api.get(`/admin/api/cloud/revenue?range=${range}`),
    getFeatures: (range = '30d') => api.get(`/admin/api/cloud/features?range=${range}`),
    getGeo: () => api.get('/admin/api/cloud/geo'),
    getInfra: (range = '24h') => api.get(`/admin/api/cloud/infra?range=${range}`),
    getAlerts: (status = 'all', limit = 50) => api.get(`/admin/api/cloud/alerts?status=${status}&limit=${limit}`),
    postCost: (month, cost, notes) => api.post('/admin/api/cloud/costs', { month, infra_cost_usd: cost, notes }),
    runQuery: (sql) => api.post('/admin/api/cloud/query', { sql }),
    getTables: () => api.get('/admin/api/cloud/tables'),
    getTable: (name, limit = 100, offset = 0) => api.get(`/admin/api/cloud/table/${name}?limit=${limit}&offset=${offset}`),
};

export const compatAPI = {
    getDevices: () => api.get('/admin/api/compat/devices'),
    createDevice: (data) => api.post('/admin/api/compat/devices', data),
    updateDevice: (id, data) => api.put(`/admin/api/compat/devices/${id}`, data),
    getVerdicts: (deviceId) => api.get(`/admin/api/compat/verdicts/${deviceId}`),
    createVerdict: (data) => api.post('/admin/api/compat/verdicts', data),
    updateVerdict: (id, data) => api.put(`/admin/api/compat/verdicts/${id}`, data),
    getUnknownOuis: () => api.get('/admin/api/oui/unknowns'),
    getMatrix: (params) => api.get('/admin/api/compat/matrix', { params: params || {} }),
    getErrors: (paramsOrRange = '30d') => {
        // Two call shapes: legacy single-string range, or new params object
        // (days, release_op, release_value, layer). The route accepts both.
        if (typeof paramsOrRange === 'string') {
            return api.get(`/admin/api/compat/errors?range=${paramsOrRange}`);
        }
        return api.get('/admin/api/compat/errors', { params: paramsOrRange });
    },
    getBuildsSeen: (days = 30) =>
        api.get('/admin/api/compat/builds_seen', { params: { days } }),
    getOuiFailures: () => api.get('/admin/api/compat/oui-failures'),
    getSinkRegistry: () => api.get('/admin/api/compat/sink-registry'),
    // Drill-down for a single error_code on the leaderboard.
    getErrorDetail: (params) =>
        api.get('/admin/api/compat/error_detail', { params: params || {} }),
};

// v1.11.20 — LiveEventsPage backend (events tail + new analytics views)
export const eventsAPI = {
    getRecent: (limit = 200, eventType = '', since = '') => {
        const params = new URLSearchParams({ limit });
        if (eventType) params.set('event_type', eventType);
        if (since)     params.set('since', since);
        return api.get(`/admin/api/events/recent?${params.toString()}`);
    },
    getEventTypeSummary: (since = '') => {
        const params = new URLSearchParams();
        if (since) params.set('since', since);
        return api.get(`/admin/api/events/event_types_summary?${params.toString()}`);
    },
    getIntegrityStats: () => api.get('/admin/api/events/integrity_stats'),
    // Per-dialog step timeline — pivots cc_connection_dialog_* events for a
    // single dialog_id into a step ladder with success/failure markers.
    getDialogTimeline: (dialogId) => api.get(`/admin/api/events/dialog/${encodeURIComponent(dialogId)}`),
    // Aggregate funnel — across many dialogs, where do connections drop off.
    getConnectionFunnel: (params) =>
        api.get('/admin/api/events/connection_funnel', { params: params || {} }),
    getCastOutcomes:  () => api.get('/admin/api/events/cast_outcomes'),
    getDeathKinds:    () => api.get('/admin/api/events/death_kinds'),
    getSourceProtocolMatrix: (days = 30, layer = 'all', extra) =>
        api.get('/admin/api/events/source_protocol_matrix', {
            params: { days, layer, ...(extra || {}) }
        }),
    getBaselineCompare: (params) =>
        api.get('/admin/api/events/baseline_compare', { params: params || {} }),
    // DMS = "TV pulls from phone" — separate axis from the main matrix
    // because the direction is reversed. Aggregates dms_* events.
    getDmsSessions: (days = 30, extra) =>
        api.get('/admin/api/events/dms_sessions', {
            params: { days, ...(extra || {}) }
        }),
};

export const manifestAPI = {
    get: () => api.get('/admin/manifest'),
    put: (manifest) => api.put('/admin/manifest', { manifest }),
    rollback: () => api.post('/admin/manifest/rollback'),
    preview: (locale) => api.get(`/v1/manifest${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`),
};

// Helper Genie — admin inbox endpoints (text + voice feedback).
// Public submit endpoints live at /v1/feedback/{text,voice} and don't
// go through this axios instance (HelperGenie posts them directly).
export const feedbackAPI = {
    list: (params = {}) => api.get('/admin/api/feedback', { params }),
    get: (id) => api.get(`/admin/api/feedback/${id}`),
    // Audio endpoint requires Authorization header, so a bare <audio src>
    // tag won't work. Fetch as blob and create an object URL for the player.
    fetchAudioBlob: (id) => api.get(`/admin/api/feedback/${id}/audio`, { responseType: 'blob' }),
    update: (id, fields) => api.patch(`/admin/api/feedback/${id}`, fields),
};

export default api;
